import { Box } from "@mui/material";
import useWavesurfer from "./useWaveSurfer";
import React, { useEffect, useRef } from "react";
import "./index.scss";

export default function WavePlayerFeed({
  url,
  handlePlay,
  handlePause,
  handleCurrentTime,
  handleEnd,
  handleSeek,
  sampleData,
  volume,
  showAdv,
  playing,
}) {
  const containerRef = useRef();
  const wavesurfer = useWavesurfer(containerRef, url);

  // Stable refs for callbacks so the event subscription effect doesn't re-run
  const handlePlayRef = useRef(handlePlay);
  const handlePauseRef = useRef(handlePause);
  const handleCurrentTimeRef = useRef(handleCurrentTime);
  const handleEndRef = useRef(handleEnd);
  const handleSeekRef = useRef(handleSeek);
  const playingRef = useRef(playing);
  useEffect(() => { handlePlayRef.current = handlePlay; }, [handlePlay]);
  useEffect(() => { handlePauseRef.current = handlePause; }, [handlePause]);
  useEffect(() => { handleCurrentTimeRef.current = handleCurrentTime; }, [handleCurrentTime]);
  useEffect(() => { handleEndRef.current = handleEnd; }, [handleEnd]);
  useEffect(() => { handleSeekRef.current = handleSeek; }, [handleSeek]);
  useEffect(() => { playingRef.current = playing; }, [playing]);

  // Set volume when it changes
  useEffect(() => {
    if (wavesurfer) {
      if (typeof wavesurfer.setVolume === 'function') {
        wavesurfer.setVolume(volume / 100);
      } else {
        const media = wavesurfer.getMediaElement();
        if (media) media.volume = volume / 100;
      }
    }
  }, [volume, wavesurfer]);

  // Play/pause based on prop, but only if state changes
  useEffect(() => {
    if (!wavesurfer) return;
    if (playing && !wavesurfer.isPlaying()) {
      wavesurfer.play().catch((e) => {
        if (e.name !== "AbortError") console.error(e);
      });
    } else if (!playing && wavesurfer.isPlaying()) {
      wavesurfer.pause();
    }
  }, [playing, wavesurfer]);

  // Handle showAdv changes if needed
  useEffect(() => {
    if (!wavesurfer) return;
    // If you want to auto-play on showAdv, you can trigger play here
    // wavesurfer.play();
  }, [showAdv, wavesurfer]);

  // Subscribe to wavesurfer events — uses refs for callbacks so this effect
  // only re-runs when wavesurfer instance or url changes, not on every render.
  useEffect(() => {
    if (!wavesurfer) return;

    const subscriptions = [
      wavesurfer.on("ready", () => {
        if (typeof wavesurfer.setVolume === 'function') {
          wavesurfer.setVolume(volume / 100);
        } else {
          const media = wavesurfer.getMediaElement();
          if (media) media.volume = volume / 100;
        }
        if (playingRef.current) {
          wavesurfer.play().catch((e) => {
            if (e.name !== "AbortError") console.error(e);
          });
        }
        handleCurrentTimeRef.current && handleCurrentTimeRef.current(0);
        handlePlayRef.current && handlePlayRef.current(true);
      }),
      wavesurfer.on("play", () => {
        handlePlayRef.current && handlePlayRef.current(true);
      }),
      wavesurfer.on("pause", () => {
        handlePauseRef.current && handlePauseRef.current(false);
      }),
      wavesurfer.on("timeupdate", (currentTime) => {
        if (handleCurrentTimeRef.current) {
          const duration = wavesurfer.getDuration ? wavesurfer.getDuration() : 0;
          handleCurrentTimeRef.current(currentTime, duration);
        }
      }),
      wavesurfer.on("interaction", (currentTime) => {
        handleSeekRef.current && handleSeekRef.current(currentTime);
      }),
      wavesurfer.on("finish", () => {
        handleEndRef.current && handleEndRef.current();
      }),
    ];

    return () => {
      subscriptions.forEach((unsub) => unsub());
    };
  }, [wavesurfer, url, volume]);

  return (
    <Box
      sx={{
        width: "100%",
        py: 2,
        px: 0,
        mb: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 600,
          height: 64,
          minHeight: 48,
          margin: "0 auto",
          borderRadius: 8,
        }}
      />
    </Box>
  );
}